import type { NativeStackScreenProps } from "@react-navigation/native-stack";
// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import { Formik } from "formik";
import _ from "lodash";
import {
  Box,
  Button,
  Center,
  FormControl,
  Input,
  KeyboardAvoidingView,
  Link,
  PresenceTransition,
  Pressable,
  Spinner,
  Text,
  useTheme,
  VStack,
} from "native-base";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import type { Routes } from "../constants";
import { LOGIN_CODE_HELP_ARTICLE_URL } from "../helpers/supportHelpers";
import type { RootStackParamList } from "../navigation/NavigationStackParams";
import backendApi from "../services/backendApi";
import { userSlice } from "../slices/userSlice";

const { useUsersVerifyOtpCreateMutation, useUsersResendOtpCreateMutation } = backendApi;

interface VerifyOTPFormValues {
  email: string;
  otp: string;
}

type Props = NativeStackScreenProps<RootStackParamList, Routes.MagicLinkSentScreen>;
const EnterOTPScreen = ({
  navigation,
  route: {
    params: { email },
  },
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [verifyOTP] = useUsersVerifyOtpCreateMutation();
  const [resendOTP] = useUsersResendOtpCreateMutation();
  const [hasResentCode, setHasResentCode] = useState(false);
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const verifyOTPFormSchema = Yup.object().shape({
    email: Yup.string().trim().email(t("onboarding.registration_screen.invalid_email")).required(t("general.required")),
    otp: Yup.string()
      .required(t("general.required"))
      .matches(/^[0-9]{4}$/, t("enter_otp_screen.yup_otp_validation_message")),
  });

  const handleResendOTP = async (): Promise<void> => {
    setIsResendingOTP(true);
    try {
      // Add minimum delay for better UX
      const minimumDelay = new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 750);
      });

      await Promise.all([
        minimumDelay,
        resendOTP({
          sendOtpRequest: {
            email,
          },
        }).unwrap(),
      ]);

      // Show success message and clear any server errors
      setHasResentCode(true);
      setServerError(null);
      console.log("OTP resent successfully");
    } catch (e: unknown) {
      Sentry.captureException(e, {
        tags: {
          screen: "EnterOTPScreen",
          action: "resendOTP",
        },
        extra: {
          email,
        },
      });

      console.error("Resend OTP error:", e);

      // Extract specific error message from backend if available
      const errorMessage = (e as { data?: { error?: string } })?.data?.error || t("login_screen.failure_recovery");
      alert(errorMessage);
    } finally {
      setIsResendingOTP(false);
    }
  };

  const initialValues: VerifyOTPFormValues = { email, otp: "" };
  const submitForm = async (values: VerifyOTPFormValues): Promise<void> => {
    try {
      const response = await verifyOTP({
        verifyOtpRequest: {
          email: values.email,
          otp: values.otp,
        },
      }).unwrap();

      const authToken = response?.auth_token;
      dispatch(userSlice.actions.storeAuthToken(authToken));
    } catch (e: unknown) {
      // Capture the error in Sentry with context
      Sentry.captureException(e, {
        tags: {
          screen: "EnterOTPScreen",
          action: "verifyOTP",
        },
        extra: {
          email: values.email,
          // Not including OTP for security reasons
        },
      });

      console.error("OTP verification error:", e);

      // Set the error message to display inline under the input
      setServerError(t("enter_otp_screen.invalid_code"));
    }
  };

  return (
    <Box flex={1} safeArea bg="function.10">
      <Formik initialValues={initialValues} validationSchema={verifyOTPFormSchema} onSubmit={submitForm}>
        {({ isSubmitting, handleChange, handleBlur, handleSubmit, values, errors, dirty, isValid }) => (
          <Center flex={1}>
            <KeyboardAvoidingView behavior="position">
              <Box p="2" w="90%" mx="auto">
                <VStack space={6} alignItems="center">
                  <VStack space={4} alignItems="center">
                    <Text fontSize="2xl" fontWeight="medium" textAlign="center">
                      {t("enter_otp_screen.heading")}
                    </Text>
                    <Text textAlign="center" color="function.40" fontSize="md">
                      {t("enter_otp_screen.subheading", { email })}
                    </Text>
                  </VStack>

                  <Box w="35%" position="relative">
                    <FormControl
                      isRequired
                      isInvalid={(!_.isEmpty(errors.otp) && values.otp.length === 4) || serverError !== null}
                    >
                      <Input
                        onChangeText={(text) => {
                          handleChange("otp")(text);
                          if (serverError) setServerError(null);
                        }}
                        onBlur={handleBlur("otp")}
                        value={values.otp}
                        placeholder={t("enter_otp_screen.otp_placeholder")}
                        testID={"otp-input"}
                        mt="6"
                        py="3.5"
                        keyboardType="number-pad"
                        textContentType="oneTimeCode"
                        textAlign="center"
                        fontSize="xl"
                        fontWeight="bold"
                        maxLength={4}
                        borderRadius={theme.borderRadii.input.default}
                        borderColor="function.20"
                        _focus={{
                          borderColor: "primary.40",
                          bg: "function.10",
                          _ios: {
                            selectionColor: "primary.40",
                          },
                          _android: {
                            selectionColor: "primary.40",
                          },
                        }}
                        _invalid={{
                          borderColor: "error.500",
                        }}
                        focusOutlineColor="primary.40"
                      />
                    </FormControl>
                    <Box position="absolute" top="100%" left="-50%" right="-50%" mt={2}>
                      <PresenceTransition
                        visible={!!serverError || (!!errors.otp && values.otp.length === 4)}
                        initial={{ opacity: 0, translateY: -10 }}
                        animate={{ opacity: 1, translateY: 0, transition: { duration: 250 } }}
                      >
                        <Center>
                          <Text color="error.500" fontSize="sm" textAlign="center">
                            {serverError || errors.otp}
                          </Text>
                        </Center>
                      </PresenceTransition>
                    </Box>
                  </Box>

                  <Button
                    isLoading={isSubmitting}
                    isDisabled={!dirty || !isValid}
                    onPress={() => handleSubmit()}
                    w="50%"
                    h={12}
                    mt={8}
                    testID="otpButton"
                    bg="primary.40"
                    borderRadius={theme.borderRadii.button.default}
                    _text={{
                      fontSize: "xl",
                      fontWeight: "bold",
                      fontFamily: theme.fonts.body,
                    }}
                    _pressed={{
                      bg: "primary.50",
                    }}
                    _disabled={{
                      bg: "primary.20",
                    }}
                    _loading={{
                      h: 12,
                      minH: 12,
                    }}
                  >
                    {t("enter_otp_screen.submit_button")}
                  </Button>

                  {hasResentCode ? (
                    <Center mt={6}>
                      <VStack space={2} alignItems="center">
                        <Text fontSize="lg" fontWeight="medium" textAlign="center">
                          {t("enter_otp_screen.new_code_sent")}
                        </Text>
                        <Text color="function.40" fontSize="md" textAlign="center">
                          {t("enter_otp_screen.check_spam_or")}
                          <Text> </Text>
                          {/* TODO: We are currently writing an updated article (modelled on Playbook's) */}
                          <Link href={LOGIN_CODE_HELP_ARTICLE_URL} isExternal>
                            <Text color="primary.40" textDecorationLine="underline">
                              {t("enter_otp_screen.get_help")}
                            </Text>
                          </Link>
                          <Text>
                            {"\n"}
                            {t("enter_otp_screen.if_not_received")}
                          </Text>
                        </Text>
                      </VStack>
                    </Center>
                  ) : (
                    <Center mt={6}>
                      {isResendingOTP ? (
                        <Spinner size="sm" color="primary.40" />
                      ) : (
                        <Text color="function.40" fontSize="md">
                          {t("enter_otp_screen.didnt_receive_one")}
                          <Text> </Text>
                          <Pressable
                            onPress={() => {
                              handleResendOTP().catch((error) => {
                                Sentry.captureException(error, {
                                  tags: {
                                    screen: "EnterOTPScreen",
                                    action: "resendOTP",
                                  },
                                });
                                console.error("Resend OTP error:", error);
                              });
                            }}
                            disabled={isResendingOTP}
                          >
                            <Text color="primary.40" fontSize="md" textDecorationLine="underline">
                              {t("enter_otp_screen.resend_code")}
                            </Text>
                          </Pressable>
                        </Text>
                      )}
                    </Center>
                  )}

                  <Button
                    mt="8"
                    variant="ghost"
                    onPress={() => navigation.goBack()}
                    _text={{
                      color: "primary.40",
                      fontWeight: "medium",
                    }}
                    _pressed={{
                      backgroundColor: theme.colors.primary[20],
                    }}
                  >
                    {t("enter_otp_screen.back_button")}
                  </Button>
                </VStack>
              </Box>
            </KeyboardAvoidingView>
          </Center>
        )}
      </Formik>
    </Box>
  );
};
export default EnterOTPScreen;
